import { Undo2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatDate, toDateKey, weekdayName } from '@/lib/dates';
import { ShiftCell } from './WeekGrid';
import type { WeekAssignment, WeekBoard, WeekStaff } from './weekApi';

/**
 * יום אחד, כל הסניפים (מסמך התכנון 6.3).
 *
 * הלוח השבועי עונה על "איך נראה השבוע בסניף". השאלה השנייה שנשאלת
 * מול אותו מסך היא "מי עובד מחר", והיא חצתה חמש טבלאות: צריך היה
 * לגלול בין סניף לסניף ולזכור מה היה בקודם.
 *
 * כאן הצירים מתהפכים — שורה לכל סניף, עמודה לכל משמרת — ואותו תא
 * בדיוק עושה את העבודה. התא יודע את הסניף, התאריך והמשמרת שלו,
 * ולכן הגרירה עובדת בלי שום שינוי.
 */
export function DayGrid({
  board,
  date,
  isDragging,
  onRemove,
  onOpen,
  onClose,
  onReopen,
  taskCounts,
  specialDays,
  highlightedId = null,
  people,
}: {
  board: WeekBoard;
  date: string;
  isDragging: boolean;
  onRemove: (assignmentId: string) => void;
  onOpen: (assignment: WeekAssignment) => void;
  onClose: (cell: { branchId: string; date: string; shiftTypeId: string }) => void;
  onReopen: (cell: { branch_id: string; date: string; shift_type_id: string | null }) => void;
  taskCounts: Map<string, number> | undefined;
  specialDays: Map<string, { name: string; kind: string }[]> | undefined;
  highlightedId?: string | null;
  people?: WeekStaff[];
}) {
  const isToday = date === toDateKey(new Date());
  const special = specialDays?.get(date) ?? [];

  const closureFor = (branchId: string, shiftTypeId: string) =>
    board.closures.find(
      (closure) =>
        closure.branch_id === branchId &&
        closure.is_closed &&
        (closure.date === date || closure.date === null) &&
        (closure.shift_type_id === null || closure.shift_type_id === shiftTypeId),
    );

  return (
    <div className="grid gap-2">
      <h2 className="m-0 flex flex-wrap items-baseline gap-2 text-title font-semibold text-ink">
        {weekdayName(date)}
        <span className="tabular text-body text-muted" dir="ltr">
          {formatDate(date)}
        </span>
        {isToday && <span className="text-label font-medium text-olive">היום</span>}
        {special.length > 0 && (
          <span className="text-label font-normal text-olive">
            {special.map((one) => one.name).join(' · ')}
          </span>
        )}
      </h2>

      <div className="overflow-x-auto rounded-sm border border-line bg-raised">
        <table className="w-full min-w-[36rem] table-fixed border-collapse">
          <thead>
            <tr>
              <th className="w-28 border-b border-line bg-cream p-1.5 text-start text-label text-muted">
                סניף
              </th>
              {board.shift_types.map((shift) => (
                <th
                  key={shift.id}
                  className="border-b border-line bg-cream p-2 text-center text-label text-muted"
                >
                  <span className="grid gap-0.5">
                    <span className="font-medium text-ink">{shift.name}</span>
                    <span className="tabular" dir="ltr">
                      {shift.start_time.slice(0, 5)}–{shift.end_time.slice(0, 5)}
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {board.branches.map((branch, index) => (
              <tr key={branch.id} className={cn(index % 2 === 1 && 'bg-cream/50')}>
                <th className="border-b border-line bg-cream p-1.5 text-start align-top">
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="size-3 shrink-0 rounded-pill border border-line"
                      style={branch.color ? { backgroundColor: branch.color } : undefined}
                    />
                    <span className="min-w-0 truncate text-body-sm font-medium text-ink">
                      {branch.name}
                    </span>
                  </span>
                </th>

                {board.shift_types.map((shift) => {
                  const closed = closureFor(branch.id, shift.id);

                  return (
                    <td key={shift.id} className="border-b border-line p-1 align-top">
                      {closed ? (
                        <p className="m-0 flex items-center gap-1 rounded-sm border border-dashed border-line px-1.5 py-1 text-label text-muted">
                          <span className="min-w-0 grow truncate">{closed.label}</span>
                          {closed.date === date && (
                            <button
                              type="button"
                              aria-label={`פתיחה מחדש של ${branch.name} ב${weekdayName(date)}`}
                              onClick={() =>
                                onReopen({
                                  branch_id: branch.id,
                                  date,
                                  shift_type_id: closed.shift_type_id,
                                })
                              }
                              className="grid size-5 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-muted hover:bg-cream hover:text-ink"
                            >
                              <Undo2 size={12} strokeWidth={2} aria-hidden="true" />
                            </button>
                          )}
                        </p>
                      ) : (
                        <ShiftCell
                          board={board}
                          branchId={branch.id}
                          date={date}
                          shiftTypeId={shift.id}
                          isDragging={isDragging}
                          onRemove={onRemove}
                          onOpen={onOpen}
                          onClose={onClose}
                          taskCounts={taskCounts}
                          highlightedId={highlightedId}
                          people={people ?? board.staff}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
